import axios from 'axios'

// Auto-detect API URL: use same host but port 8000 for sandbox, or /api/v1 for production proxy
const getApiBase = () => {
  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL
  const host = window.location.hostname
  if (host.includes('sandbox') || host.includes('novita') || host.includes('e2b')) {
    // Sandbox environment: direct to backend port 8000
    return `${window.location.protocol}//${host.replace(/^(\d+)-/, '8000-')}/api/v1`
  }
  return '/api/v1'
}

const API_BASE = getApiBase()

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

// ─── Ontology ────────────────────────────────────────────────────────────────
export const ontologyApi = {
  getClasses: () => api.get('/ontology/classes'),
  getProperties: () => api.get('/ontology/properties'),
  getClass: (name: string) => api.get(`/ontology/classes/${name}`),
  getStats: () => api.get('/ontology/stats'),
}

// ─── Learning ────────────────────────────────────────────────────────────────
export const learningApi = {
  getSteps: () => api.get('/learning/steps'),
  getStep: (id: string) => api.get(`/learning/steps/${id}`),
  getLearningPath: (from: string, to: string) => api.get(`/learning/path/${from}/${to}`),
}

// ─── Tutor ───────────────────────────────────────────────────────────────────
export const tutorApi = {
  analyzeAnswer: (data: Record<string, unknown>) => api.post('/tutor/analyze', data),
  getStepExplanation: (stepId: string) => api.get(`/tutor/step/${stepId}/explanation`),
  generatePractice: (stepId: string, difficulty: string = 'medium') =>
    api.post('/tutor/practice', { step_id: stepId, difficulty }),
  evaluateFunction: (slope: number, intercept: number) =>
    api.post('/tutor/evaluate-function', { slope_value: slope, intercept_value: intercept }),
  getFeedback: (misconceptionId: string) => api.get(`/tutor/feedback/${misconceptionId}`),
  getMisconceptions: () => api.get('/tutor/misconceptions'),
}

// ─── Data ────────────────────────────────────────────────────────────────────
export const dataApi = {
  getFunctions: () => api.get('/data/functions'),
  getFunction: (id: string) => api.get(`/data/functions/${id}`),
  getFunctionTable: (id: string, xStart = -3, xEnd = 3) =>
    api.get(`/data/functions/${id}/table?x_start=${xStart}&x_end=${xEnd}`),
  getGraphPoints: (id: string) => api.get(`/data/functions/${id}/graph-points`),
}

// ─── Rules ───────────────────────────────────────────────────────────────────
export const rulesApi = {
  getRules: () => api.get('/rules/'),
  getRule: (id: string) => api.get(`/rules/${id}`),
  getRulesByCategory: (category: string) => api.get(`/rules/category/${category}`),
}

// ─── MCP ────────────────────────────────────────────────────────────────────
export const mcpApi = {
  getTools: () => api.get('/mcp/tools'),
  invoke: (tool: string, parameters: Record<string, unknown>) =>
    api.post('/mcp/invoke', { tool, parameters }),
  chat: (role: string, content: string, studentId?: string, stepId?: string) =>
    api.post('/mcp/chat', { role, content, student_id: studentId, step_id: stepId }),
}

// ─── System ───────────────────────────────────────────────────────────────────
export const systemApi = {
  getOverview: () => api.get('/system/overview'),
  getHealth: () => axios.get('/health'),
  getRoot: () => axios.get('/'),
}
